import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PersonIcon from "@mui/icons-material/PersonTwoTone";
import { Typography, Grid, Stack, Button, Divider } from "@financeable-com-au/financeable-ui";
import { getIsConsumerOrPersonalApplication } from "@/constants";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import {
  applicationSelector,
  confirmCustomerData,
  entitySelector,
  removeCustomer,
  sendCustomerDetailsLinkToCustomer,
  userDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import type {
  ApplicationFormSlice,
  ApplicationState,
  UserDetailsState,
  Entity,
  Customer,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { customerSelector } from "@/store/slices/customerSlice";
import type { CustomerSlice, ContactData } from "@/store/slices/types/customerSlice";
// @ts-expect-error: ignoring as it is existing js file
import CustomerContactDetails from "./CustomerContactDetails";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPersonalDetails from "./CustomerPersonalDetails";
// @ts-expect-error: ignoring as it is existing js file
import CustomerPersonalProfile from "./CustomerPersonalProfile";
import { CustomerInviteActions } from "./CustomerInviteActions";
import { EmploymentDetailsCondensed, type CustomerEmployer } from "./EmploymentDetailsCondensed";
import QuoteCreditScore from "../Quote/QuoteComponents/QuoteCreditScore";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "../Utils/helperFunction";

interface CustomerDetailsCondensedProps {
  customer: Customer;
  applicationId: string;
  applicationType: ApplicationType;
  index: number;
  fieldErrors: Record<string, string>[];
  setFieldErrors: (errors: Record<string, string>[] | Record<string, string>) => void;
}

const CustomerDetailsCondensed = React.memo(
  ({ customer, applicationId, applicationType, index, fieldErrors, setFieldErrors }: CustomerDetailsCondensedProps) => {
    const dispatch = useDispatch();
    const entity = useSelector<ApplicationFormSlice, Entity[]>(entitySelector);
    const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
    const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);
    const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
    const { isCustomerLogin } = useSelector<CustomerSlice, ContactData>(customerSelector);
    const { confirmCustomerDataLoader } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);
    const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;
    const customerId = customer?._id;
    const [confirmRemoveCustomer, setConfirmRemoveCustomer] = useState<Record<string, boolean>>({});

    const handle = {
      sendFormLinkCustomer: ({
        customerId,
        applicationId,
        applicationType,
      }: {
        customerId: string;
        applicationId: string;
        applicationType: ApplicationType;
      }) => {
        dispatch(sendCustomerDetailsLinkToCustomer({ customerId, applicationId, applicationType }));
      },
      confirmCustomerChanges: () => {
        dispatch(
          confirmCustomerData({
            customerId: customer._id,
            data: {
              application: applicationId,
              isCustomerDetailsSaved: false,
              isDetailsLinkSend: false,
            },
          }),
        );
      },
    };

    const isSendLinkBtnDisable =
      !customer?.firstName ||
      !customer?.dateOfBirth ||
      customer?.emails?.length <= 0 ||
      customer?.phoneNumbers?.length <= 0 ||
      isDisableEdit;

    const { role, appointmentDate, shares } = customer;
    const personalDetailsKey = applicationId + "-" + index;

    return (
      <Grid container xs={12} padding={0} pb={8} id="customer-section">
        <Grid xs={12} md={2}>
          <Stack direction="row" mb={5}>
            <PersonIcon style={{ marginRight: "15px", color: "#1d4164" }} />
            <Typography level="body-md">
              {getCustomerRole(application.applicationType, application.entities, application.purpose)} #{index + 1}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            {role && (
              <Typography level="body-sm">
                <strong>Role:</strong> <br />
                {role}
              </Typography>
            )}
            {appointmentDate && (
              <Typography level="body-sm">
                <strong>Appointment date:</strong> <br />
                {appointmentDate}
              </Typography>
            )}
            {shares && entity?.[0]?.shares?.[0]?.numberOfShares && (
              <Typography level="body-sm">
                <strong>Shares:</strong> <br />
                {shares}/{entity?.[0]?.shares?.[0]?.numberOfShares} (
                {((shares / entity?.[0]?.shares?.[0]?.numberOfShares) * 100).toFixed(2)}
                )%
              </Typography>
            )}
          </Stack>
          <Stack gap={2} mt={2} alignItems="flex-start">
            {!isCustomerLogin && !confirmRemoveCustomer[customer._id] && (
              <Button
                variant="secondary"
                size="sm"
                disabled={isDisableEdit}
                onClick={() => setConfirmRemoveCustomer({ [customer._id]: true })}
              >
                Remove
              </Button>
            )}

            {customer?.isDetailsLinkSend && customer?.isCustomerDetailsSaved && (
              <Button
                variant="secondary"
                size="sm"
                disabled={isSendLinkBtnDisable || confirmCustomerDataLoader}
                onClick={handle.confirmCustomerChanges}
              >
                {confirmCustomerDataLoader ? "Saving..." : "Confirm"}
              </Button>
            )}

            {confirmRemoveCustomer[customer._id] && (
              <>
                <Typography mt={4}>Are you sure?</Typography>
                <Stack alignItems="flex-start">
                  <Button
                    variant="inline"
                    size="sm"
                    onClick={() => setConfirmRemoveCustomer({ [customer._id]: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="inline"
                    size="sm"
                    intent="danger"
                    onClick={() => {
                      dispatch(
                        removeCustomer({
                          customerId: customer._id,
                          applicationId,
                        }),
                      );
                      setConfirmRemoveCustomer({ [customer._id]: false });
                      setFieldErrors({
                        customerTitle: "",
                        firstName: "",
                        lastName: "",
                        IDNumber: "",
                        dateOfBirth: "",
                        IDCardNumber: "",
                        phoneNumber: "",
                        emailAddress: "",
                        address: "",
                      });
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </>
            )}

            {!isCustomerLogin && customer?.isDetailsLinkSend && (
              <Button
                variant="secondary"
                size="sm"
                disabled={isSendLinkBtnDisable}
                onClick={() =>
                  handle.sendFormLinkCustomer({
                    customerId: customer._id,
                    applicationId,
                    applicationType,
                  })
                }
              >
                Resend link
              </Button>
            )}
          </Stack>

          <CustomerInviteActions
            customer={customer}
            applicationId={applicationId}
            applicationType={applicationType}
            isCustomerLogin={!!isCustomerLogin}
            isSendLinkBtnDisable={isSendLinkBtnDisable}
            isDisableEdit={isDisableEdit}
          />
        </Grid>

        <Grid container xs={12} md={10} style={{ padding: "0 0 0 10px" }}>
          <CustomerPersonalDetails
            customer={customer}
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            applicationId={applicationId}
            key={personalDetailsKey}
            isDisableEdit={isDisableEdit}
          />
          <CustomerContactDetails
            customerId={customerId}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            customer={customer}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          <CustomerPersonalProfile
            customer={customer}
            customerId={customerId}
            applicationId={applicationId}
            isDisableEdit={isDisableEdit}
          />
          <Stack flexDirection="column" rowGap={8} width="100%">
            <Stack flexDirection="column" rowGap={2} width="50%">
              <Typography level="title-sm" textColor="neutral.700">
                Credit Profile
              </Typography>
              <QuoteCreditScore applicationId={applicationId} disabled={isDisableEdit} />
            </Stack>
            {isConsumerOrPersonalApplication &&
              customer?.employers?.map((employer: CustomerEmployer, employerIndex: number) => (
                <>
                  <Divider />
                  <Stack rowGap={2}>
                    <Typography level="title-sm" textColor="neutral.700">
                      Employment
                    </Typography>
                    <EmploymentDetailsCondensed
                      key={employer?._id ?? employer?.id ?? employerIndex}
                      customer={customer}
                      employer={employer}
                      index={employerIndex}
                      applicationId={applicationId}
                      fieldErrors={fieldErrors}
                      setFieldErrors={setFieldErrors}
                      isDisableEdit={isDisableEdit}
                      isCustomerLogin={!!isCustomerLogin}
                    />
                  </Stack>
                </>
              ))}
          </Stack>
        </Grid>
      </Grid>
    );
  },
);

export { CustomerDetailsCondensed };
